import { IAuditLog } from "../../../../@types/AuditLog";

/**
 * Returns the changed-fields map, or null when it isn't one (missing,
 * unparseable, an array, or a non-object) — the caller then falls back to
 * the generic "Made changes." message, as Retool did.
 */
function parseChangedFields(
  raw: IAuditLog["changedFields"]
): Record<string, unknown> | null {
  if (!raw) return null;

  let parsed: unknown = raw;

  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }

  return parsed as Record<string, unknown>;
}

/**
 * Human-readable one-liner for an audit row — the portal port of Retool's
 * `moduleAuditLogs/lib/getActionDetails.js` (`generateActivityMessage`),
 * which backed the module table's "Action" column. Kept message-for-message
 * identical so a migrated panel and the Retool page read the same for the
 * same row.
 *
 * `changedFields` arrives as a JSON string (`{ field: { old, new } }`); it is
 * parsed defensively because the audit trigger can also write an `_error`
 * marker or a plain array, neither of which names fields.
 */
export function describeAuditAction(
  log: Pick<IAuditLog, "operation" | "changedFields"> | null | undefined
): string {
  if (!log || !log.operation) {
    return "Invalid operation";
  }

  switch (log.operation.toUpperCase()) {
    case "READ":
      return "Viewed details.";

    case "UPDATE": {
      const changedFields = parseChangedFields(log.changedFields);
      if (!changedFields) {
        return "Made changes.";
      }

      const fieldNames = Object.keys(changedFields);
      if (fieldNames.length === 0) {
        return "Made changes.";
      }

      return `Changed ${fieldNames.join(", ")}.`;
    }

    case "INSERT":
      return "Created.";

    case "DELETE":
      return "Deleted.";

    default:
      return "Performed an unknown operation.";
  }
}
